import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   ABHA / ABDM PATIENT PORTAL MODULE
═══════════════════════════════════════════════════════════ */

function AbhaIdTab({ linked, onGenerate, onLink }) {
  const [form, setForm] = useState({ name: '', mobile: '', dob: '', gender: 'M', aadhaar: '', email: '' });
  const [generated, setGenerated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linkForm, setLinkForm] = useState({ abhaId: '', patient: '', uhid: '' });

  async function generateAbha(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const seq = String(Date.now()).slice(-8);
      const id = `HREG-${seq}`;
      const row = { abhaId: id, name: form.name, mobile: form.mobile, gender: form.gender, dob: form.dob, registryType: 'internal' };
      await onGenerate(row);
      setGenerated({ ...row });
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="info-banner mb">
        Internal health ID registry only — not connected to national ABDM/ABHA APIs yet. Use Link ABHA for patients who already have a government ABHA ID.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card" style={{ borderLeft: '4px solid #06b6d4' }}>
          <h3 style={{ marginTop: 0, color: '#0891b2' }}>🆔 Register internal health ID</h3>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14, padding: '8px 12px', background: '#f0f9ff', borderRadius: 8 }}>
            ABHA (Ayushman Bharat Health Account) provides a unique 14-digit health ID for every Indian citizen, enabling secure health record sharing across healthcare providers.
          </div>
          <form onSubmit={generateAbha}>
            <div className="form-grid mb">
              <div className="field full"><label>Full Name *</label><input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="As per Aadhaar" /></div>
              <div className="field"><label>Mobile *</label><input className="input" required value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="10-digit" /></div>
              <div className="field"><label>Date of Birth *</label><input type="date" className="input" required value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} /></div>
              <div className="field"><label>Gender</label><select className="input" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option></select></div>
              <div className="field"><label>Aadhaar (last 4 digits)</label><input className="input" maxLength={4} value={form.aadhaar} onChange={e => setForm(f => ({ ...f, aadhaar: e.target.value }))} placeholder="XXXX" /></div>
              <div className="field"><label>Email</label><input type="email" className="input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Optional" /></div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? '⟳ Saving...' : '🆔 Register internal health ID'}</button>
          </form>
          {generated && (
            <div style={{ marginTop: 16, padding: '16px 18px', background: 'linear-gradient(135deg, #0891b2, #0d6e6a)', borderRadius: 14, color: '#fff' }}>
              <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Internal Health ID saved ✓</div>
              <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 28, fontWeight: 900, letterSpacing: '0.15em', marginBottom: 6 }}>{generated.abhaId}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 8 }}>Hospital registry ID (HREG-…) — not a government ABHA number. Link a real ABHA ID on the right if patient has one.</div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>{generated.name} · {generated.gender === 'M' ? 'Male' : 'Female'} · {generated.dob}</div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button type="button" style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 }} onClick={() => onLink({ abhaId: generated.abhaId, patient: generated.name, uhid: `PT-${Date.now()}`, registryType: 'internal' })}>🔗 Link to Patient</button>
                <button type="button" style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 }} disabled title="ABDM card print requires national ABHA integration">🖨 Print ABHA Card</button>
              </div>
            </div>
          )}
        </div>
        <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
          <h3 style={{ marginTop: 0, color: '#059669' }}>🔗 Link Existing ABHA ID</h3>
          <div className="form-grid mb">
            <div className="field full"><label>ABHA ID</label><input className="input" value={linkForm.abhaId} onChange={e => setLinkForm(f => ({ ...f, abhaId: e.target.value }))} placeholder="XX-XXXX-XXXX-XXXX" /></div>
            <div className="field"><label>Patient Name</label><input className="input" value={linkForm.patient} onChange={e => setLinkForm(f => ({ ...f, patient: e.target.value }))} /></div>
            <div className="field"><label>UHID</label><input className="input" value={linkForm.uhid} onChange={e => setLinkForm(f => ({ ...f, uhid: e.target.value }))} /></div>
          </div>
          <button type="button" className="btn btn-primary btn-sm mb" onClick={() => { if (!linkForm.abhaId || !linkForm.patient) return; onLink(linkForm); setLinkForm({ abhaId: '', patient: '', uhid: '' }); }}>🔗 Link ABHA ID</button>
          <h4 style={{ marginTop: 12 }}>Linked ABHA IDs</h4>
          {linked.map(l => (
            <div key={l.abhaId || l.recordId} style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, color: '#0891b2', fontSize: 13 }}>{l.abhaId}</div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{l.patient} · {l.uhid}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }}>{l.status || 'Active'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConsentTab({ requests, onPatch }) {
  const sColor = { Pending: '#f59e0b', Approved: '#10b981', Rejected: '#dc2626' };

  return (
    <div>
      <div className="info-banner mb">
        Internal consent workflow only — not connected to national ABDM Consent Manager. Approvals here are hospital records for audit; they do not share data on the ABDM network.
      </div>
      <div style={{ padding: '12px 16px', background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#0c4a6e' }}>
        🔒 <strong>Consent Management:</strong> Track patient consent requests for health data sharing within your hospital workflow.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requests.map(r => (
          <div key={r.id || r.recordId} className="card" style={{ borderLeft: `4px solid ${sColor[r.status]}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 800 }}>{r.patient} <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#0891b2', fontWeight: 400 }}>{r.abhaId}</span></div>
                <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>📍 Requested by: <strong>{r.requestedBy}</strong></div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Purpose: {r.purpose} · Records: {r.records}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Valid till: {r.validTill}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: sColor[r.status] + '18', color: sColor[r.status], border: `1px solid ${sColor[r.status]}40` }}>{r.status}</span>
                {r.status === 'Pending' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => onPatch(r.recordId || r.id, { status: 'Approved' })}>✓ Approve</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => onPatch(r.recordId || r.id, { status: 'Rejected' })}>✕ Reject</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AbdmStatusTab() {
  const checks = [
    { label: 'Health Facility Registry (HFR) Registration', status: false, detail: 'Requires ABDM HFR credentials — not configured' },
    { label: 'Healthcare Professional Registry (HPR)', status: false, detail: 'Requires ABDM HPR integration — not configured' },
    { label: 'ABDM Sandbox / Production API', status: false, detail: 'No ABDM client ID or gateway configured in Lambda' },
    { label: 'ABHA Generation (national OTP)', status: false, detail: 'Use Link ABHA for patients with existing government ABHA' },
    { label: 'PHR App Linkage', status: false, detail: 'Pending ABDM PHR integration' },
    { label: 'FHIR R4 Record Export', status: false, detail: 'Planned — not connected to ABDM HIP' },
    { label: 'Consent Manager Integration', status: false, detail: 'Internal workflow only; national CM not connected' },
    { label: 'DigiLocker Integration', status: false, detail: 'Not configured' },
  ];
  const passed = checks.filter((c) => c.status).length;

  return (
    <div>
      <div className="info-banner mb">
        ABDM national integration is <strong>not live</strong> for this deployment. The checklist below reflects actual readiness — connect ABDM credentials in AWS to enable national features.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Checks Passed', val: passed, color: '#10b981' }, { label: 'Not Connected', val: checks.length - passed, color: '#f59e0b' }, { label: 'Readiness', val: `${Math.round(passed / checks.length * 100)}%`, color: '#6366f1' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>ABDM Integration Status</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {checks.map(c => (
            <div key={c.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: c.status ? '#f0fdf4' : '#fffbeb', border: `1px solid ${c.status ? '#86efac' : '#fde68a'}` }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: c.status ? '#16a34a' : '#92400e' }}>{c.status ? '✓' : '⏳'} {c.label}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{c.detail}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: c.status ? '#dcfce7' : '#fef9c3', color: c.status ? '#16a34a' : '#ca8a04' }}>{c.status ? 'Live' : 'Not connected'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AbhaPage() {
  const { records: linked, create: createLink } = useHmsRecords('abha-link');
  const { records: requests, create: createConsent, patch } = useHmsRecords('abha-consent');
  const [tab, setTab] = useState('abha');

  const linkAbha = async (row) => {
    await createLink({ ...row, linkedAt: new Date().toISOString().slice(0, 10), status: 'Active' });
  };

  return (
    <PageShell
      title="ABHA / ABDM Integration"
      subtitle="Ayushman Bharat Health Account · Consent-based Health Record Sharing · FHIR R4"
      actions={<a href="https://abha.abdm.gov.in" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">🔗 ABDM Portal</a>}
    >
      <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 10, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 24 }}>🏥</span>
        <div>
          <div style={{ fontWeight: 800, color: '#92400e', fontSize: 13 }}>ABDM national ecosystem — integration pending</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>This module stores internal health IDs and consent records. National ABHA (14-digit) and ABDM record sharing require gateway credentials and HFR registration.</div>
        </div>
      </div>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'abha', label: '🆔 ABHA ID' }, { id: 'consent', label: '🔒 Consent Management' }, { id: 'status', label: '📡 ABDM Status' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'abha' && <AbhaIdTab linked={linked} onGenerate={linkAbha} onLink={linkAbha} />}
      {tab === 'consent' && <ConsentTab requests={requests} onPatch={patch} />}
      {tab === 'status' && <AbdmStatusTab />}
    </PageShell>
  );
}
